/**
 * Filter constants - labels and configurations for filter UI
 */

import type { FilterOperator, RelativeDate, ColumnType } from '../columns/types';

// Operator labels for display
export const OPERATOR_LABELS: Record<FilterOperator, string> = {
  eq: 'is',
  neq: 'is not',
  gt: 'greater than',
  gte: 'at least',
  lt: 'less than',
  lte: 'at most',
  contains: 'contains',
  not_contains: 'does not contain',
  starts_with: 'starts with',
  ends_with: 'ends with',
  in: 'is any of',
  not_in: 'is none of',
  between: 'is between',
  exists: 'exists',
  not_exists: 'is empty',
};

// Relative date labels for display
export const RELATIVE_DATE_LABELS: Record<RelativeDate, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  tomorrow: 'Tomorrow',
  this_week: 'This week',
  last_week: 'Last week',
  next_week: 'Next week',
  this_month: 'This month',
  last_month: 'Last month',
  next_month: 'Next month',
  this_year: 'This year',
  last_year: 'Last year',
  last_7_days: 'Last 7 days',
  last_30_days: 'Last 30 days',
  last_90_days: 'Last 90 days',
  last_365_days: 'Last 365 days',
};

// Operators available for each column type
export const OPERATORS_BY_TYPE: Record<ColumnType, FilterOperator[]> = {
  text: ['eq', 'neq', 'contains', 'not_contains', 'starts_with', 'ends_with', 'exists', 'not_exists'],
  number: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', 'exists', 'not_exists'],
  date: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', 'exists', 'not_exists'],
  boolean: ['eq', 'neq', 'exists', 'not_exists'],
  select: ['eq', 'neq', 'in', 'not_in', 'exists', 'not_exists'],
  multiselect: ['contains', 'not_contains', 'exists', 'not_exists'],
  image: ['exists', 'not_exists'],
  url: ['eq', 'neq', 'contains', 'exists', 'not_exists'],
  currency: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', 'exists', 'not_exists'],
};

// Default operator for each column type
export const DEFAULT_OPERATOR_BY_TYPE: Record<ColumnType, FilterOperator> = {
  text: 'contains',
  number: 'eq',
  date: 'eq',
  boolean: 'eq',
  select: 'eq',
  multiselect: 'contains',
  image: 'exists',
  url: 'eq',
  currency: 'eq',
};

// Check if an operator requires a value input
export function operatorNeedsValue(operator: FilterOperator): boolean {
  return operator !== 'exists' && operator !== 'not_exists';
}

// Check if this is a date-relative operator (uses relativeTo instead of value)
export function isDateRelativeFilter(operator: FilterOperator): boolean {
  return ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between'].includes(operator);
}
